import { useEffect, useState, type CSSProperties } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Share2, User } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { formatDisplayDate } from "@/lib/dateFormat";
import { sanitizeRichTextHtml } from "@/lib/htmlSanitizer";
import { toWordPreview } from "@/lib/textPreview";
import { buildShareUrl } from "@/lib/shareUrl";
import { shareContent } from "@/lib/shareContent";

const fontFamilyByStyle: Record<string, string> = {
  normal: "'Inter', system-ui, sans-serif",
  times_new_roman: "'Times New Roman', Times, serif",
  courier_new: "'Courier New', Courier, monospace",
};

const NewsDetail = () => {
  const { id } = useParams();
  const { news } = useData();
  const [isSharing, setIsSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  // Find news by slug first, then by ID for backward compatibility
  const item = news.find((n) => n.slug === id) || news.find((n) => n.id === id);
  const safeSummary = sanitizeRichTextHtml(item?.summary || "");
  const safeContent = sanitizeRichTextHtml(item?.content || "");
  const textAlign = (item?.contentAlignment || "justify") as CSSProperties["textAlign"];
  const fontFamily = fontFamilyByStyle[item?.fontStyle || "times_new_roman"] || fontFamilyByStyle.times_new_roman;
  const articleTextStyle: CSSProperties = { textAlign, fontFamily };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = item ? `${item.title} | dkonnects` : "Article not found | dkonnects";

    return () => {
      document.title = previousTitle;
    };
  }, [item]);

  useEffect(() => {
    const faviconHref = item?.image ? String(item.image).trim() : "data:,";
    const selectors = 'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]';
    const existingLinks = Array.from(document.head.querySelectorAll<HTMLLinkElement>(selectors));
    const snapshots = existingLinks.map((el) => ({
      el,
      href: el.getAttribute("href"),
      rel: el.getAttribute("rel"),
      type: el.getAttribute("type"),
      sizes: el.getAttribute("sizes"),
    }));
    const createdLinks: HTMLLinkElement[] = [];
    const targets = existingLinks.length > 0 ? existingLinks : (() => {
      const link = document.createElement("link");
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
      createdLinks.push(link);
      return [link];
    })();

    targets.forEach((linkEl) => {
      linkEl.setAttribute("href", faviconHref);
      const rel = String(linkEl.getAttribute("rel") || "").toLowerCase();
      if (rel === "icon" && !linkEl.getAttribute("sizes")) {
        linkEl.setAttribute("sizes", "any");
      }
    });

    return () => {
      snapshots.forEach(({ el, href, rel, type, sizes }) => {
        if (href === null) {
          el.removeAttribute("href");
        } else {
          el.setAttribute("href", href);
        }

        if (rel === null) {
          el.removeAttribute("rel");
        } else {
          el.setAttribute("rel", rel);
        }

        if (type === null) {
          el.removeAttribute("type");
        } else {
          el.setAttribute("type", type);
        }

        if (sizes === null) {
          el.removeAttribute("sizes");
        } else {
          el.setAttribute("sizes", sizes);
        }
      });

      createdLinks.forEach((el) => el.remove());
    };
  }, [item?.image]);

  const handleShare = async () => {
    if (!item) {
      return;
    }

    setIsSharing(true);
    setShareMessage("");

    const shareUrl = buildShareUrl("news", item.slug || item.id);
    const result = await shareContent({
      title: item.title,
      text: toWordPreview(item.summary || item.content || "", 20),
      url: shareUrl,
      imageUrl: item.image,
    });

    if (result.status === "copied") {
      setShareMessage("News link copied to clipboard.");
    } else if (result.status === "unsupported") {
      setShareMessage("Sharing is not supported in this browser.");
    } else if (result.status === "error") {
      setShareMessage("Unable to share right now. Please try again.");
    }

    setIsSharing(false);
  };

  if (!item) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">Article not found.</p>
        <Link to="/news" className="text-secondary hover:underline mt-4 inline-block">← Back to News</Link>
      </div>
    );
  }

  return (
    <div className="py-10 md:py-14">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/news" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" style={articleTextStyle}><ArrowLeft size={16} /> Back to News</Link>
        <span className="badge-info mb-4">{item.category}</span>
        
        {/* News Image */}
        {item.image && (
          <div className="mb-6">
            <img 
              src={item.image} 
              alt={item.title}
              className="w-full h-64 md:h-80 object-cover rounded-lg"
              onError={(e) => {
                console.log("News image failed to load:", item.image);
                e.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
        )}
        
        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-4 mt-2" style={articleTextStyle}>{item.title}</h1>
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground mb-8" style={articleTextStyle}>
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><User size={14} /> {item.author}</span>
            <span className="flex items-center gap-1"><Clock size={14} /> {formatDisplayDate(item.date)}</span>
          </div>
          <button
            type="button"
            onClick={handleShare}
            disabled={isSharing}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-input bg-background text-foreground hover:bg-muted disabled:opacity-60"
          >
            <Share2 size={14} />
            {isSharing ? "Sharing..." : "Share"}
          </button>
        </div>
        {shareMessage && <p className="text-xs text-muted-foreground -mt-5 mb-5">{shareMessage}</p>}
        <div className="prose prose-sm max-w-none text-foreground" style={articleTextStyle}>
          <div className="news-richtext text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: safeSummary }} />
          {safeContent && <div className="mt-4 news-richtext" dangerouslySetInnerHTML={{ __html: safeContent }} />}
        </div>
        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border" style={articleTextStyle}>
          <p className="text-xs text-muted-foreground"><strong>Disclaimer:</strong> News articles are for informational purposes only. We do not guarantee completeness or accuracy.</p>
        </div>
      </div>
    </div>
  );
};

export default NewsDetail;
